import type { Clock } from './clock';

/** 60Hz fixed update step, in milliseconds. */
export const FIXED_STEP_MS = 1000 / 60;

/**
 * Guard against the spiral of death after a backgrounded tab: never simulate more than this many
 * fixed steps for one frame. On an iPhone the tab is backgrounded constantly.
 */
const MAX_STEPS_PER_FRAME = 8;

/**
 * 1000/60 is not representable in binary floating point, so an accumulator that has taken on
 * exactly N steps' worth of time can land a few ulps short of N * FIXED_STEP_MS and silently drop
 * a step. Over a 90-second round that loses roughly a second. Compare with a tolerance far below
 * one step but far above the accumulated error.
 */
const STEP_EPSILON_MS = 1e-9;

/**
 * Something that calls back once per display frame. `RafFrameSource` in the browser;
 * `ManualFrameSource` in tests, where frames are pumped synchronously so no test ever waits on
 * real elapsed time.
 */
export interface FrameSource {
  start(onFrame: () => void): void;
  stop(): void;
}

export class RafFrameSource implements FrameSource {
  private handle: number | null = null;

  start(onFrame: () => void): void {
    const tick = (): void => {
      this.handle = requestAnimationFrame(tick);
      onFrame();
    };
    this.handle = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.handle !== null) cancelAnimationFrame(this.handle);
    this.handle = null;
  }
}

/** A frame source that only fires when a test says so. */
export class ManualFrameSource implements FrameSource {
  private onFrame: (() => void) | null = null;

  start(onFrame: () => void): void {
    this.onFrame = onFrame;
  }

  stop(): void {
    this.onFrame = null;
  }

  /** Fire one frame. No-op if the loop is stopped. */
  pump(): void {
    this.onFrame?.();
  }
}

export interface LoopOptions {
  clock: Clock;
  frames: FrameSource;
  /** Fixed-timestep simulation. `stepMs` is always exactly FIXED_STEP_MS. */
  update: (stepMs: number) => void;
  /** Interpolated presentation. `alpha` is the fraction of a step since the last update. */
  render: (alpha: number) => void;
}

/**
 * Separated update and render loops (BRIEF <architecture>).
 *
 * Update runs on a fixed 60Hz accumulator so simulation is frame-rate independent and
 * reproducible; render runs once per frame with an interpolation alpha. Gamepad polling belongs
 * in `update`, never in `render`.
 */
export class GameLoop {
  private readonly opts: LoopOptions;
  private accumulatorMs = 0;
  private lastNow = 0;
  private running = false;
  private tickCount = 0;

  constructor(opts: LoopOptions) {
    this.opts = opts;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastNow = this.opts.clock.now();
    this.accumulatorMs = 0;
    this.opts.frames.start(() => this.frame());
  }

  stop(): void {
    this.running = false;
    this.opts.frames.stop();
  }

  /** Total fixed updates run since construction. The harness asserts on this. */
  ticks(): number {
    return this.tickCount;
  }

  /**
   * One frame: drain elapsed real time into whole fixed steps, then render once.
   * Public so the harness can drive it without a frame source.
   */
  frame(): void {
    if (!this.running) return;

    const now = this.opts.clock.now();
    let elapsed = now - this.lastNow;
    this.lastNow = now;

    // A clock that jumped backwards (manual setNow, or a device clock correction) must not
    // rewind the simulation.
    if (elapsed < 0) elapsed = 0;

    this.accumulatorMs += elapsed;

    let steps = 0;
    while (
      this.accumulatorMs + STEP_EPSILON_MS >= FIXED_STEP_MS &&
      steps < MAX_STEPS_PER_FRAME
    ) {
      this.opts.update(FIXED_STEP_MS);
      this.accumulatorMs -= FIXED_STEP_MS;
      this.tickCount++;
      steps++;
    }

    // Drop the backlog rather than fast-forwarding through it after a long background.
    if (this.accumulatorMs > FIXED_STEP_MS * MAX_STEPS_PER_FRAME) {
      this.accumulatorMs = 0;
    }

    this.opts.render(this.accumulatorMs / FIXED_STEP_MS);
  }
}
